import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthSession } from 'aws-amplify/auth';

import { fetchSubscriptionStatus, selectSubscriptionStatus } from '../store/userSlice';
import {
  fetchProcessedModelSchemas,
  selectProcessedModelSchemaCount,
  selectProcessedModelSchemasLoaded,
  selectProcessedModelSchemasListLoading,
} from '../store/processedModelSchemaSlice';
import {
  checkProcessedModelLimit,
  PROCESSED_MODEL_TIER_CAP_ENABLED,
  ProcessedModelLimitCheck,
} from '../api/stripeSubscription';
import type { AppDispatch } from '../store';

export interface ProcessedModelLimitGate {
  /** True once both the subscription tier and the model count are known — the cap is not enforced before then. */
  ready: boolean;
  /** undefined until the Cognito groups are read (or when that read failed): callers keep the Admin convention then */
  isAdmin: boolean | undefined;
  /** The cap result, or null when the user is exempt or the data isn't loaded yet (fail open). */
  evaluate: (modelsToAdd?: number) => ProcessedModelLimitCheck | null;
}

/**
 * Shared membership-tier gate for deploying processed models. Warms the subscription + schema caches on
 * mount so the check is instant at click time, and fails open (returns null from `evaluate`) for Admins
 * or while either cache is still loading — matching the asset-limit flows. Frontend-only: the backend
 * does not yet enforce this cap.
 */
export const useProcessedModelLimit = (): ProcessedModelLimitGate => {
  const dispatch = useDispatch<AppDispatch>();
  const subscriptionStatus = useSelector(selectSubscriptionStatus);
  const schemaCount = useSelector(selectProcessedModelSchemaCount);
  const schemasLoaded = useSelector(selectProcessedModelSchemasLoaded);
  const schemasListLoading = useSelector(selectProcessedModelSchemasListLoading);
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);

  // cognito:groups don't change mid-session, so read them once — a failed read leaves false and the cap still applies.
  useEffect(() => {
    let isMounted = true;
    fetchAuthSession()
      .then((session) => {
        const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] as string[] | undefined;
        if (isMounted) setIsAdmin(groups?.includes('Admin') ?? false);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  // Warm both caches so the check is instant at click time; both thunks dedupe in-flight dispatches.
  useEffect(() => {
    dispatch(fetchSubscriptionStatus());
    if (schemasListLoading === 'idle') dispatch(fetchProcessedModelSchemas());
  }, [dispatch, schemasListLoading]);

  const ready = Boolean(subscriptionStatus) && schemasLoaded;

  const evaluate = useCallback(
    (modelsToAdd = 1) => {
      if (!PROCESSED_MODEL_TIER_CAP_ENABLED || isAdmin || !ready) return null;
      return checkProcessedModelLimit(subscriptionStatus, schemaCount, modelsToAdd);
    },
    [isAdmin, ready, subscriptionStatus, schemaCount]
  );

  return { ready, isAdmin, evaluate };
};
